import React from 'react';
import { Stack, Typography, DataGrid, Tooltip, DATAGRID_HEADER_HEIGHT } from '../../../ui/core';
import { useFormatters } from '../../../hooks';
import { TripStatus } from '../../../models/Trips';
import UserCard from './UserCard';
import TimeRangeVisual from './TimeRangeVisual';
import TripTags from './TripTags';
import TripETA from './TripETA';
import { isGhostTrip } from '../../../helpers/utils/trip';

import { StationIcon, DollarBadgeIcon, EventIcon, InfoIcon, RouteIcon, PersonIcon, CarIcon, LuggageIcon, WarningIcon } from '../../../ui/icons';
import CheckCircleIcon from '../../../ui/icons/CheckCircleIcon';
import CircleIcon from '../../../ui/icons/CircleIcon';
import DottedCircleIcon from '../../../ui/icons/DottedCircleIcon';
import SearchCircleIcon from '../../../ui/icons/SearchCircleIcon';
import { theme } from '../../../ui/styles';

const style = {
  dataGrid: {
    // Group/section headers stick just below the sticky column-header row so the
    // status title stays locked and visible while scrolling its section — and
    // lands cleanly when the status rail scrolls to it. Offset by the shared
    // header-height constant so it can't drift from the actual header height.
    '.DataGrid-row-group': {
      position: 'sticky',
      top: `${DATAGRID_HEADER_HEIGHT}px`,
      // Above the trip rows (the return/previous/next rows sit at 2), and
      // opaque: the grid's own row tint is translucent in dark mode, so rows
      // scrolling underneath showed through the header. Same colours as the
      // mobile list's sticky headers.
      zIndex: 3,
      backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#212124' : '#F2F2F3'),
    },
    '.trip--return, .trip--previous, .trip--next': {
      position: 'relative',
      backgroundColor: (theme) =>
        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)',
      '&:hover, &.Mui-selected': {
        backgroundColor: (theme) => theme.palette.action.selected,
      },
      zIndex: 2,
      color: (theme) => theme.palette.text.secondary,
      '.DataGrid-cell': {
        overflow: 'visible',
      },
    },
    '.trip--return, .trip--next': {
      '.trip__departure': {
        '> div:first-of-type': {
          position: 'absolute',
          left: '16px',
          bottom: '16px',
        },
      },
    },
    '.trip--previous': {
      '.trip__departure': {
        '> div:first-of-type': {
          position: 'absolute',
          left: '16px',
          top: '16px',
        },
      },
    },
    // HITCH-5300: informational, not a blocking error — a subtle warning tint
    // (not red) so an ops call the icon alone would be too easy to miss can
    // still see it at a glance while scanning the whole table.
    '.trip--excluded-pair': {
      backgroundColor: (theme) =>
        theme.palette.mode === 'dark' ? 'rgba(255, 167, 38, 0.10)' : 'rgba(255, 167, 38, 0.08)',
      '&:hover, &.Mui-selected': {
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255, 167, 38, 0.16)' : 'rgba(255, 167, 38, 0.14)',
      },
    },
  },
};

// Which riders (if any) on this row are excluded with its driver — HITCH-5300
// decorates rows with `excludedRiderIds` via TripsTable's `getRow` prop, but
// unclaimed/previous/next-leg rows may never carry the field at all.
const excludedRiderIdsOf = (row) => row?.excludedRiderIds;

const renderExclusionWarningIcon = (row) => {
  const excludedRiderIds = excludedRiderIdsOf(row);
  if (!excludedRiderIds || excludedRiderIds.size === 0) return null;
  const names = (row.rides || [])
    .filter((ride) => excludedRiderIds.has(ride.rider.id))
    .map((ride) => `${ride.rider.firstName} ${ride.rider.lastName}`.trim())
    .join(', ');
  return (
    <Tooltip title={`Excluded pairing with ${names} — see Rider-Driver Exclusions.`} arrow>
      <span style={{ display: 'inline-flex' }}>
        <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
      </span>
    </Tooltip>
  );
};

const renderConfirmationStatusIcon = (status) => {
  const sx = { fontSize: 16 };
  let icon = null;
  let label = null;
  switch (status) {
    case 'PRE_CONFIRMATION':
      icon = <DottedCircleIcon sx={{ ...sx, color: 'text.disabled' }} />;
      label = 'Pre-Confirmation';
      break;
    case 'CONFIRMED':
      icon = <CheckCircleIcon color="success" sx={sx} />;
      label = 'Confirmed';
      break;
    case 'UNCONFIRMED':
      icon = <CircleIcon sx={{ ...sx, color: 'text.disabled' }} />;
      label = 'Unconfirmed';
      break;
    case 'PENDING_REASSIGNMENT':
      icon = <SearchCircleIcon color="error" sx={sx} />;
      label = 'Pending Reassignment';
      break;
    default:
      return null;
  }
  return (
    <Tooltip title={label} arrow>
      <span style={{ display: 'inline-flex' }}>{icon}</span>
    </Tooltip>
  );
};

export const TRIP_COLUMNS = [
  { field: 'id', hide: true },
  { field: 'departureDate', hide: true },
  {
    field: 'departure',
    headerName: 'Departure',
    headerIcon: <EventIcon />,
    width: 160,
    renderCell: ({ row }) => {
      if (row.isPrevious || row.isNext) {
        return (
          <span className="trip__departure">
            <TimeRangeVisual />
            <Typography variant="body2" color="text.secondary">
              {row.departure}
            </Typography>
          </span>
        );
      }
      return <Typography>{row.departure}</Typography>;
    },
  },
  {
    field: 'eta',
    headerName: 'ETA',
    headerIcon: <EventIcon />,
    width: 160,
    renderCell: ({ row }) => {
      if (row.isPrevious || row.isNext || row.status !== TripStatus.in_progress) {
        return <span></span>;
      }
      return <TripETA trip={row} />;
    },
  },
  {
    // The grouping key, so this column has to stay in the list — but it no longer
    // prints the status. The group header it sits under already names the status and
    // is sticky for the length of its section, and the toolbar filters cut by the
    // same vocabulary, so a per-row chip was the same fact a third time. It was also
    // the WEAKER fact: the header shows `displayStatus`, the chip showed
    // `trip.status`, so every row under Finding Driver, Extended Bonus and Needs
    // Attention read a flat "Processing".
    //
    // Tags are not status and are the one thing here you can't get from the header,
    // so they stay and the column is titled for what it now shows. (TripStatusChip
    // itself is unchanged and still used where there's no group header to lean on —
    // the trip detail dialog and a user's drive history.)
    field: 'displayStatus',
    headerName: 'Tags',
    headerIcon: <InfoIcon />,
    width: 140,
    renderCell: ({ row }) =>
      row.isNext || row.isPrevious ? <span></span> : <TripTags tags={row.tags} />,
  },
  {
    field: 'route',
    headerName: 'Route',
    headerIcon: <RouteIcon />,
    flex: 2,
    renderCell: ({ row }) => {
      if (row.isPrevious || row.isNext) {
        return (
          <Stack direction="row" alignItems="center">
            <Typography>{row.route}</Typography>
            <Stack direction="row" alignItems="center" ml="14px" spacing="4px">
              <StationIcon sx={{ height: '16px', width: '16px', color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {row.rides.length}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" ml="14px" spacing="4px">
              <DollarBadgeIcon sx={{ height: '16px', width: '16px' }} />
              <Typography variant="body2" color="text.secondary">
                {row.payout}
              </Typography>
            </Stack>
          </Stack>
        );
      }
      return (
        <Stack direction="row" alignItems="center">
          {row.isGhostTrip && <Typography sx={{ mr: '8px' }}>👻</Typography>}
          <Typography>{row.route}</Typography>
          <Stack direction="row" alignItems="center" ml="14px" spacing="4px">
            <StationIcon sx={{ height: '16px', width: '16px', color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {row.rides.length}
            </Typography>
          </Stack>
        </Stack>
      );
    },
  },
  {
    field: 'driver',
    headerName: 'Driver',
    headerIcon: <PersonIcon />,
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => {
      if (row.isPrevious || row.isNext) {
        return <span></span>;
      }
      if (!row.driver) return null;
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <UserCard user={row.driver} />
          {renderConfirmationStatusIcon(row.confirmationStatus)}
          {renderExclusionWarningIcon(row)}
        </Stack>
      );
    },
  },
  {
    field: 'fleetVehicle',
    headerName: 'Fleet Vehicle',
    headerIcon: <CarIcon />,
    flex: 1,
    minWidth: 170,
    renderCell: ({ row }) => {
      if (row.isPrevious || row.isNext) {
        return <span></span>;
      }
      if (!row.fleetVehicle) return null;
      return (
        <Stack direction="column" spacing={0} sx={{ lineHeight: 1.2 }}>
          <Typography sx={{ fontWeight: 500 }}>{row.fleetVehicle.displayName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {[row.fleetVehicle.year, row.fleetVehicle.make, row.fleetVehicle.model]
              .filter(Boolean)
              .join(' ')}
          </Typography>
        </Stack>
      );
    },
  },
  {
    field: 'riders',
    headerName: 'Passengers',
    headerIcon: <LuggageIcon />,
    flex: 2,
    renderCell: ({ row }) => {
      if (row.isPrevious || row.isNext) {
        return <span></span>;
      }
      const getRideStatusText = (status, riderFirstName) => {
        switch (status) {
          case 'completed':
            return 'Ride completed';
          case 'en_route':
            return `${riderFirstName} is being dropped off`;
          case 'accepted':
            return `${riderFirstName} is being picked up`;
          case 'processing':
            return 'Awaiting driver acceptance';
          case 'finalizing_details':
            return 'Awaiting match time';
          default:
            return '';
        }
      };

      return (
        <Stack direction="row" spacing={1}>
          {row.rides.map((ride) => {
            const rideId = ride.id || ride.rideId;
            const key = `${rideId}_${ride.rider.id}`;
            const isExcluded = excludedRiderIdsOf(row)?.has(ride.rider.id);
            const rideStatusText = getRideStatusText(ride.status, ride.rider.firstName);
            const tooltipTitle = isExcluded
              ? `Excluded pairing with this trip's driver.${rideStatusText ? ` ${rideStatusText}` : ''}`
              : rideStatusText;

            return (
              <Stack key={key} direction="row" alignItems="center" spacing={0.5}>
                {ride.status && ride.status !== 'processing' && ride.status !== 'finalizing_details' && (
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor:
                        ride.status === 'completed'
                          ? theme.palette.success.main
                          : ride.status === 'en_route'
                          ? theme.palette.error.main
                          : ride.status === 'accepted'
                          ? theme.palette.accent.main
                          : theme.palette.accent.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px',
                    }}
                  >
                    {ride.status === 'completed' ? '✓' : ride.status === 'en_route' ? '↓' : '↑'}
                  </div>
                )}
                <Tooltip title={tooltipTitle} arrow>
                  <Typography
                    sx={{
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                      cursor: 'help',
                      ...(isExcluded && { color: 'warning.main', fontWeight: 600 }),
                    }}
                  >{`${ride.rider.firstName} ${ride.rider.lastName}`}</Typography>
                </Tooltip>
                {isExcluded && <WarningIcon sx={{ fontSize: 14, color: 'warning.main' }} />}
              </Stack>
            );
          })}
        </Stack>
      );
    },
  },
  {
    field: 'payout',
    headerName: 'Payout',
    headerIcon: <DollarBadgeIcon />,
    flex: 1,
    minWidth: 140,
    renderCell: ({ row }) => (row.isPrevious || row.isNext ? <span></span> : <Typography>{row.payout}</Typography>),
  },
  {
    field: 'manualBonus',
    headerName: 'Manual Bonus',
    headerIcon: <DollarBadgeIcon />,
    flex: 1,
    minWidth: 170,
    renderCell: ({ row }) => (row.isPrevious || row.isNext ? <span></span> : <Typography>{row.manualBonus}</Typography>),
  },
];

const TripsTable = ({ data, loading, getColumn, getRow, onRowClick, ghostTripsOnly }) => {
  const f = useFormatters();

  const columns = getColumn ? TRIP_COLUMNS.map((c) => getColumn({ ...c })) : TRIP_COLUMNS;

  let rows = data
    .map((t) => ({
      trip: t,
      previousTrip: data.find((ot) => ot.id === t.previousTrip),
      returnTrip: data.find((rt) => rt.tripType === 'return' && t.id === rt.previousTrip),
      isReturn: t.tripType === 'return' && data.find((ot) => ot.id === t.previousTrip),
    }))
    .reduce((_rows, d) => {
      const getRow = (t, { isPrevious = false, isNext = false } = {}) => {
        const tripIsGhost = isGhostTrip(t);

        // Calculate departure display based on ghost filter and ghost leg departure time
        let departureDisplay;
        if (ghostTripsOnly && tripIsGhost && t.ghostLegDepartureTime) {
          const ghostTime = f.date.toTimezoneLocaleString(
            t.ghostLegDepartureTime,
            t.originCity.timeZone,
            f.date.DateFormats.TIME_SIMPLE_WITH_TIMEZONE,
          );
          const returnTime = f.date.toTimezoneLocaleString(
            t.departureTime,
            t.originCity.timeZone,
            f.date.DateFormats.TIME_SIMPLE_WITH_TIMEZONE,
          );
          departureDisplay = `${ghostTime} - ${returnTime}`;
        } else {
          departureDisplay = f.date.toTimezoneLocaleString(
            t.departureTime,
            t.originCity.timeZone,
            f.date.DateFormats.TIME_SIMPLE_WITH_TIMEZONE,
          );
        }

        return {
          id: t.id,
          internalId: t.internalId || t.id,
          departureTime: t.departureTime,
          departure: departureDisplay,
          route: `${t.originCity.cityName} ➔ ${t.destinationCity.cityName}`,
          driver: t.driver,
          status: t.status,
          displayStatus: t.displayStatus,
          rides: t.rides,
          payout: f.number.currency(t.payout || 0),
          manualBonus: f.number.currency(t.payoutItemized?.manualBonus || 0),
          isPrevious,
          isNext,
          tags: t.tags,
          currentWaypoint: t.currentWaypoint,
          waypoints: t.waypoints,
          originCity: t.originCity,
          isGhostTrip: tripIsGhost,
          fleetVehicle: t.fleetVehicle,
          confirmationStatus: t.confirmationStatus,
        };
      };

      if (d.returnTrip) {
        _rows.push(getRow(d.trip));
        _rows.push(
          getRow(
            {
              ...d.returnTrip,
              displayStatus: d.trip.displayStatus,
              internalId: `${d.trip.id}_next`,
            },
            { isNext: true },
          ),
        );
        return _rows;
      }

      if (d.isReturn && d.previousTrip && d.previousTrip.displayStatus !== d.trip.displayStatus) {
        _rows.push(
          getRow(
            {
              ...d.previousTrip,
              displayStatus: d.trip.displayStatus,
              internalId: `${d.trip.id}_previous`,
            },
            { isPrevious: true },
          ),
        );
        _rows.push(getRow(d.trip));
        return _rows;
      }

      _rows.push(getRow(d.trip));
      return _rows;
    }, []);

  if (getRow) {
    rows = rows.map((row) => getRow(row));
  }

  return (
    <DataGrid
      sx={style.dataGrid}
      loading={loading}
      rows={rows}
      columns={columns}
      onRowClick={onRowClick}
      hideFooterPagination
      getRowClassName={({ row }) => {
        if (row.isPrevious) {
          return 'trip--previous';
        }
        if (row.isNext) {
          return 'trip--next';
        }
        if (excludedRiderIdsOf(row)?.size > 0) {
          return 'trip--excluded-pair';
        }
        return '';
      }}
      getRowHeight={({ model }) => (model.isPrevious || model.isNext ? 40 : 60)}
      getRowId={(row) => row.internalId}
    />
  );
};

export default TripsTable;
